// Registers the glTF loader plugin for its side effect. XrManager.ts imports the same module
// for controller models, and because ES module side effects run once at load time regardless of
// how many files import them, that alone is already enough to register it before this module's
// own preload runs. Imported again here anyway: this module's preload has to run very early in
// boot (before the first node is built, well before XrManager's own construction), and nothing
// should make "does the glTF loader work" depend on XrManager.ts happening to be reachable
// first. See CLAUDE.md, "Babylon features need explicit imports".
import '@babylonjs/loaders/glTF/2.0';

import { DracoCompression } from '@babylonjs/core/Meshes/Compression/dracoCompression';
import { ImportMeshAsync } from '@babylonjs/core/Loading/sceneLoader';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Material } from '@babylonjs/core/Materials/material';
import type { Scene } from '@babylonjs/core/scene';

import type { NodeTypeDto } from '../generated/contracts';

/**
 * All eight of the models in public/models/ use KHR_draco_mesh_compression, so Babylon's glTF
 * loader needs a Draco decoder to read any of them. Left at its default, `DracoCompression`
 * fetches that decoder from `https://cdn.babylonjs.com/...` — a second, external origin this
 * single-origin app otherwise has no dependency on (CLAUDE.md's forwarded-headers and
 * same-origin-cookie notes both lean on there being exactly one), and one a Quest with no route
 * to the public internet, or a CDN outage, would silently turn into "the model failed to load",
 * indistinguishable from a genuinely missing or broken file.
 *
 * The decoder files are already inside the @babylonjs/core dependency this project already has
 * (node_modules/@babylonjs/core/assets/Draco/), copied from there into public/draco/ rather than
 * fetched a second time from anywhere. Only wasmUrl and wasmBinaryUrl are pointed locally —
 * fallbackUrl, the ~500KB pure-JavaScript decoder Babylon falls back to when WebAssembly is
 * unavailable, is deliberately left unset. Every browser this product targets (the Quest
 * browser, and a desktop browser for the two-tab multiplayer test CLAUDE.md describes) has had
 * WebAssembly for years — WebXR itself is not old enough to run anywhere that predates it — so
 * carrying dead weight for a browser this app could not otherwise run in buys nothing.
 */
DracoCompression.Configuration = {
  decoder: {
    wasmUrl: '/draco/draco_wasm_wrapper_gltf.js',
    wasmBinaryUrl: '/draco/draco_decoder_gltf.wasm',
  },
};

/**
 * Hand-sized, matching Silhouettes.ts's own SIZE — the size every other node already is.
 *
 * Exported because `core/GridSnap.ts` reads it as its cell size (plan/grid.md: "Cells are
 * 0.3 m, and that is not a round number picked for tidiness — it is TARGET_SIZE"). Two
 * shapes in adjacent cells therefore touch exactly, the same reason this value is used here
 * unchanged rather than restated as a second literal that could drift from it.
 */
export const TARGET_SIZE = 0.3;

/** Where a model's authored scale stops being "whatever the artist used" and starts looking
 *  like a mistake worth a log line — needing more than a 10x correction either way. */
const NORMAL_SCALE_FACTOR = { min: 0.1, max: 10 };

const MODEL_URL_BASE = '/models/';

/**
 * The catalogue keys this build ships a `.glb` for — named by hand, purely for `summary()`
 * below, deliberately not used anywhere in the loading or fallback logic above (preloadAll
 * still attempts every catalogue type and falls back silently regardless of whether its key
 * appears here).
 *
 * The rest of this file goes out of its way *not* to have a client-side "does this type have a
 * model" flag — a type with nothing behind it 404s exactly like one that failed to load, by
 * design (plan/diagramming.md, "no separate 'has a model' flag"). That is correct for loading,
 * and it is exactly the problem for *diagnosis*: a loaded/attempted ratio over the whole
 * catalogue can't be read at a glance, because most catalogue types were never meant to have a
 * model, so a healthy ratio and a broken one look similar without already knowing which types
 * are which. That ambiguity is what let every model 404 in production and reach the owner as a
 * headset report instead of a status line — see CLAUDE.md, ".glb has no default content type" —
 * and diagnosing it after the fact meant grepping a deployed JS bundle by hand.
 *
 * So this list exists once, only to answer "how many of the models we know we shipped actually
 * came up," for `SessionScreen` (ui/SessionScreen.ts, `setModelStatus`). If it drifts — a model
 * added or removed here without updating this list — the readout is off by a small, visible
 * amount until someone notices; that is a far cheaper failure than the ambiguity it replaces.
 *
 * Deliberately catalogue-only, not "every key this class has ever loaded": `loadFromPath`
 * (below) lets a non-catalogue caller populate the same `templates` map under a key of its own
 * choosing — AvatarManager does, for its avatar parts — and `summary()` simply ignores anything
 * outside this list rather than folding it into "expected/loaded" (see the "does not count a
 * loaded key outside the expected list" test in ModelLibrary.test.ts, which pins exactly this).
 * An avatar-part readout, if one is ever wanted, needs its own fixed list and its own summary
 * the same shape as this one — not a second meaning bolted onto this list, which exists for one
 * already-shipped, already-ambiguous failure mode this file's own history is named after.
 */
export const EXPECTED_MODEL_KEYS: readonly string[] = [
  'storage_database',
  'compute_web-server',
  'compute_container',
  'network_switch',
  'network_firewall',
  'client_user',
  'external_cloud',
  'ops_queue',
  'client_browser',
  'client_headset',
  'client_mobile',
  'network_load-balancer',
  'ops_monitoring',
  'storage_object-store',
];

/** What `summary()` reports — see `EXPECTED_MODEL_KEYS` for what "expected" means and why it
 *  has to be a fixed list rather than something derived at runtime. */
export interface ModelLoadSummary {
  /** How many of this build's known model-backed types ought to have a template. */
  expected: number;
  /** How many of those actually resolved to a usable template. */
  loaded: number;
  /** Which expected keys did not resolve — silently missing (no bytes at all, the ordinary
   *  shape of "not deployed" or "blocked", like the .glb content-type bug) or logged via
   *  console.warn (bytes arrived but could not become a mesh). This list does not distinguish
   *  the two; either way the key belongs in a status line, not just a console. */
  failedKeys: string[];
}

/**
 * Optional behaviour for `loadFromPath`/`loadFromBytes`, layered on top of the catalogue's own
 * defaults rather than replacing them — every existing call site (`preloadOne`, and therefore
 * `preloadAll`) passes no options at all and keeps normalising exactly as it always has.
 */
export interface LoadModelOptions {
  /**
   * Centre the merged mesh on its own bounding box and uniformly scale it so its largest axis
   * becomes TARGET_SIZE. Defaults to true, which is the catalogue's own unconditional behaviour
   * today — a catalogue node's whole visual identity is being one of a family of same-sized
   * props (Silhouettes.ts's own SIZE), each placed by its own geometric centre.
   *
   * AvatarManager.ts passes `false` for every avatar part it preloads (head, body, hand): an
   * avatar part is authored at its true, real-world scale — a head is not the same size as a
   * hand, so forcing all three to TARGET_SIZE would render the hand larger than the head (see
   * AVATAR_MODEL_KEYS in AvatarManager.ts for the actual sizes involved).
   *
   * Worth being deliberate about: `false` skips *centring* along with scaling, not scaling
   * alone. An avatar part's authored origin is not its geometry's bounding-box centre, it is
   * wherever that part's tracked anchor has to land — the tracked head point for a head, the
   * wrist for a hand, and only incidentally the body's own centre for the body — three different
   * answers this class has no way to tell apart from geometry alone. Recomputing the origin from
   * the bounding box would be silently wrong for a hand in particular (a hand's bounding-box
   * centre sits in the palm, nowhere near the wrist joint `AvatarManager.applyHand` positions the
   * mesh at), so the authored transform is trusted whole — no scale correction and no
   * re-centring — rather than corrected on one axis and left alone on the other.
   */
  normalize?: boolean;

  /**
   * Keeps every sub-material a merge would otherwise collapse into one — `Mesh.MergeMeshes`'s
   * own `multiMultiMaterial` argument, passed straight through. Defaults to `false`, which is
   * the catalogue's own unconditional behaviour today: a catalogue node's whole material is
   * discarded after merge and replaced by `NodeMeshFactory`'s own per-node `StandardMaterial`
   * (see `buildTemplate`'s own closing comment), so there has never been a reason to keep more
   * than one.
   *
   * `AvatarManager.ts` passes `true` for every avatar part it preloads: the owner authors each
   * of `head.glb`/`body.glb`/`hand.glb` in Blender with two material slots —
   * `Mat.Avatar.Body` (the authored white) and `Mat.Avatar.Tint` (the per-peer accent, recoloured
   * per peer in `AvatarManager.ts`) — and without this, `Mesh.MergeMeshes`'s own
   * `multiMultiMaterial` default of `false` collapses both slots into one material at load time,
   * which is exactly the "whole avatar renders in one flat colour" bug this option exists to
   * fix. When `true`, `buildTemplate` also skips discarding the merged material afterward — the
   * template's `.material` is a `MultiMaterial` a caller can inspect by name, not `null`.
   *
   * Independent of `normalize`: an avatar part passes both, but a hypothetical future
   * catalogue type with more than one material slot could ask for this alone.
   */
  preserveMaterials?: boolean;
}

/**
 * Preloaded, ready-to-clone geometry for the catalogue's model-backed types — and, since
 * `loadFromPath` below, for anything else that wants a `.glb` without pretending to be one.
 *
 * One template mesh per key that has a `.glb` behind it, built once at boot (`preloadAll`, or
 * `loadFromPath` directly for a non-catalogue caller) rather than on first use — node creation
 * is synchronous (NodeMeshFactory.create), and the splash screen exists precisely so entering VR
 * has no load left to do (CLAUDE.md, "The splash screen exists so entering VR is instant"). The
 * same rule is why AvatarManager preloads its own avatar-part models up front too, through this
 * same class (AvatarManager.ts, `preloadModels`) — Avatar's construction is just as synchronous
 * as NodeMeshFactory.create is.
 *
 * Every catalogue type is attempted, not just the eight known to have a model: a type with
 * nothing behind it simply gets a 404 and is silently skipped. That is what lets a model be
 * found from nothing but the catalogue's existing Key and Category — no new field on
 * NodeTypeDto says "this one has a model," which would be a second thing to keep in sync with
 * whatever actually sits in public/models/. A non-catalogue caller gets the same property for
 * free: a path with nothing behind it is exactly as silent and exactly as safe to attempt.
 */
export class ModelLibrary {
  private readonly templates = new Map<string, Mesh>();

  constructor(private readonly scene: Scene) {}

  /** The template for a key, if its model loaded successfully — a catalogue type's own `Key`,
   *  or a non-catalogue caller's chosen key (loadFromPath). Undefined for every type with no
   *  model behind it (most of the catalogue, by design, and every avatar part today) or whose
   *  model could not be turned into a usable mesh — either way, the caller's cue to fall back to
   *  its own procedural stand-in (a silhouette for a node; a primitive for an avatar part). */
  templateFor(key: string): Mesh | undefined {
    return this.templates.get(key);
  }

  /** Boot-time load outcome for `SessionScreen` — see `EXPECTED_MODEL_KEYS` and
   *  `ModelLoadSummary` for what "expected" means. A pure read of `this.templates` against that
   *  fixed list, independent of what preloadAll was actually called with, so it is exactly as
   *  meaningful whether it is read once at boot or ten times in a test. */
  summary(): ModelLoadSummary {
    const failedKeys = EXPECTED_MODEL_KEYS.filter((key) => !this.templates.has(key));

    return {
      expected: EXPECTED_MODEL_KEYS.length,
      loaded: EXPECTED_MODEL_KEYS.length - failedKeys.length,
      failedKeys,
    };
  }

  /** Attempts every type in the catalogue in parallel and logs one summary line — see the class
   *  doc for why a miss is the ordinary case and not logged per type. */
  async preloadAll(types: NodeTypeDto[]): Promise<void> {
    const outcomes = await Promise.all(types.map((type) => this.preloadOne(type)));
    const loaded = outcomes.filter(Boolean).length;

    console.info(
      `Model preload: ${loaded}/${types.length} catalogue type(s) resolved to a .glb model; ` +
        'the rest render their procedural silhouette.',
    );
  }

  /** A catalogue type's own path convention, handed to the general loadFromPath below — the
   *  catalogue is now just one caller of it, not a separate implementation. */
  private async preloadOne(type: NodeTypeDto): Promise<boolean> {
    return this.loadFromPath(type.key, `${MODEL_URL_BASE}${type.key}.glb`);
  }

  /**
   * Loads one model reached by an explicit path, with no NodeTypeDto involved anywhere in the
   * call — what a caller with no catalogue entry to be keyed off (an avatar part; any other
   * future non-catalogue model) uses instead of pretending to have one. `preloadOne` above is
   * now itself just such a caller: it derives the catalogue's own `${MODEL_URL_BASE}${key}.glb`
   * path and hands it here rather than fetching independently, so the eight catalogue models
   * already verified in a real browser (see CLAUDE.md, the static-asset-content-type note, and
   * plan/diagramming.md's Verify section) are live proof this path works, not a parallel,
   * separately-trusted one.
   *
   * Same map, same lookup, same fallback contract as loadFromBytes/templateFor throughout this
   * class: keyed by `key` for later retrieval via `templateFor(key)`, and a path with nothing
   * behind it — or bytes that do not resolve to a usable mesh — resolves this to `false` rather
   * than throwing, indistinguishable from a catalogue type with no model. None of that
   * reasoning is catalogue-specific, so it applies unchanged to a caller-chosen key and an
   * arbitrary path.
   *
   * `options` is forwarded to loadFromBytes/buildTemplate untouched — see LoadModelOptions for
   * what it controls. Omitted here by every catalogue call (preloadOne), which is what keeps the
   * eight shipped models normalising exactly as before.
   */
  async loadFromPath(key: string, path: string, options?: LoadModelOptions): Promise<boolean> {
    const bytes = await fetchModelBytes(path);

    if (!bytes) {
      // No file at this path — expected for most of the catalogue, and for every avatar part
      // today (AvatarManager.ts, AVATAR_MODEL_KEYS).
      return false;
    }

    return this.loadFromBytes(key, bytes, options);
  }

  /**
   * Parses, merges and — by default — normalises one model's raw bytes into a template, keyed
   * for later cloning. Pass `{ normalize: false }` (LoadModelOptions) to keep the merged mesh at
   * exactly the scale and position it was authored at, rather than centred and rescaled to
   * TARGET_SIZE; AvatarManager.ts does, for its avatar parts.
   *
   * Split out from preloadOne (which fetches the bytes first) because it is also the seam
   * ModelLibrary.test.ts uses to exercise real parsing, merging and normalising without a
   * server to fetch from — the fetch step itself has nothing left to prove once fetchModelBytes
   * is covered, and there is no dev server running under vitest to fetch a real file from.
   */
  async loadFromBytes(key: string, bytes: Uint8Array, options?: LoadModelOptions): Promise<boolean> {
    try {
      this.templates.set(key, await this.buildTemplate(key, bytes, options));
      return true;
    } catch (error) {
      // Unlike a missing file, a key that reaches here *has* bytes and still failed to become a
      // usable node — worth surfacing, since every other outcome in preloadAll is silent by
      // design.
      console.warn(
        `Model "${key}.glb" was found but could not be used; falling back to its silhouette.`,
        error,
      );
      return false;
    }
  }

  /** Loads one .glb's bytes into one template mesh — centred and TARGET_SIZE-normalised unless
   *  `options.normalize` is false, in which case the mesh keeps exactly the scale and position
   *  it was authored at (LoadModelOptions). */
  private async buildTemplate(key: string, bytes: Uint8Array, options?: LoadModelOptions): Promise<Mesh> {
    const result = await ImportMeshAsync(bytes, this.scene, { pluginExtension: '.glb' });

    const parts = result.meshes.filter(
      (mesh): mesh is Mesh => mesh instanceof Mesh && mesh.getTotalVertices() > 0,
    );

    if (parts.length === 0) {
      throw new Error('glb contained no mesh geometry.');
    }

    // Babylon's glTF loader reconciles glTF's right-handed space with this project's
    // left-handed scene (CLAUDE.md, "Coordinates: left-handed") by parenting every imported
    // node under a transform-only "__root__" carrying rotation [0,1,0,0] + scale [1,1,-1] —
    // net effect, a single-axis mirror, determinant -1 (GLTFLoader.prototype._createRootNode,
    // the GLTFLoaderCoordinateSystemMode.AUTO branch, gated on `!scene.useRightHandedSystem`,
    // which this project never sets, so the branch always runs). Captured *before* merging,
    // because MergeMeshes disposes `parts` as a side effect (disposeSource: true below) and a
    // disposed mesh's computeWorldMatrix is not something to lean on.
    const sourceDeterminant = parts[0]!.computeWorldMatrix(true).determinant();

    // One mesh per node is load-bearing (CLAUDE.md): grab reach measures one bounding sphere,
    // the held look sets alpha and emissive on one mesh.material, and edges anchor to one
    // position. A loaded glb arrives as a hierarchy — here, at minimum, the dropped root plus
    // the real part(s) — so it is merged exactly the way Silhouettes.ts already merges
    // multi-part procedural shapes (same call shape: dispose the sources, allow 32-bit indices).
    //
    // The sixth argument, multiMultiMaterial, is options.preserveMaterials — see that option's
    // own doc (LoadModelOptions) for why it exists and what it changes. Left at MergeMeshes' own
    // default (false) for every catalogue call, which keeps the eight shipped models merging
    // exactly as before.
    const merged = Mesh.MergeMeshes(parts, true, true, undefined, false, options?.preserveMaterials ?? false);

    if (!merged) {
      throw new Error('Could not merge glb parts into one mesh.');
    }

    // MergeMeshes bakes every source part's *world* matrix into the merged vertex data — via
    // Mesh.computeWorldMatrix(true), so __root__'s mirror above is included — and it already
    // reverses each part's index winding when that world matrix has a negative determinant
    // (VertexData._mergeCoroutine, internally: `if (transform.determinant() < 0)
    // VertexData._FlipFaces(...)`), so positions, normals and winding all stay mutually
    // consistent with one another exactly as they were before the bake — but, found while
    // extending the facing test to a genuine multi-part model (ModelLibrary.test.ts,
    // "keeps every face correctly facing after a multi-material merge, not just a plain one"),
    // only when there are 2+ parts. Read from `_mergeCoroutine`'s own source directly, not
    // inferred: the first part's own indices only ever go through that reallocate-and-flip
    // branch when there is a *second* part's indices to append after them — with exactly one
    // part, `indices.length` already equals `totalIndices` before the check runs, so the branch
    // that flips a part's own winding never executes for it at all, determinant or no.
    //
    // What survives the merge unexamined, in both cases, is `sideOrientation`:
    // `meshSubclass.sideOrientation = source.sideOrientation` carries over the *first* part's
    // value verbatim. For glTF-loaded content in a left-handed scene, that value is always
    // Material.ClockWiseSideOrientation — GLTFLoader sets it deliberately (glTFLoader.js:
    // `useRightHandedSystem ? CCW : CW`) to pair with the still-*live*, unbaked __root__ mirror:
    // Babylon's own render path (Mesh.prototype.render) reads a mesh's current world-matrix
    // determinant every frame and flips the effective side orientation whenever it is negative
    // (`mainDeterminant < 0 ? swap CW/CCW`), which is what makes an *unmerged* glTF import
    // render correctly without this file's help. Baking removes exactly the transform that
    // flip was compensating for — the merged mesh's own world matrix is freshly identity, i.e.
    // positive determinant — so the carried-over sideOrientation stops being paired with
    // anything and, left alone, the mesh renders back-to-front: confirmed empirically (not just
    // by reading source), by loading a hand-authored, correctly-wound glb through this exact
    // path in a real WebGL browser and comparing its baked winding against its baked stored
    // normals against a known-good native Babylon mesh's own — see ModelLibrary.test.ts,
    // "facing".
    //
    // The fix is not to re-flip the geometry — MergeMeshes already got that right — it is to
    // flip sideOrientation itself, exactly once, restoring the same effective front/back result
    // the live, unmerged hierarchy already had. But *only* for the exactly-one-part case: for
    // 2+ parts, `_mergeCoroutine` has already, independently and correctly, flipped each part's
    // own winding to compensate for its own baked-in mirror — so the geometry alone is already
    // self-consistent with the *un*flipped `sideOrientation` MergeMeshes left it with, and
    // flipping it again here would cancel a compensation that already happened at the geometry
    // level, landing back on backwards. Confirmed the same way as the original fix: a
    // multi-part facing fixture failed this project's own calibrated facing rule
    // (`windingOpposesStoredNormal !== isClockwise`) with the unconditional flip in place, and
    // passed once it was scoped to `parts.length === 1`.
    if (parts.length === 1 && sourceDeterminant < 0) {
      merged.sideOrientation =
        merged.sideOrientation === Material.ClockWiseSideOrientation
          ? Material.CounterClockWiseSideOrientation
          : Material.ClockWiseSideOrientation;
    }

    // Everything ImportMeshAsync returned that was not real geometry — at minimum glTF's own
    // transform-only "__root__" node, the parent every part above just came from — is not
    // itself handed to MergeMeshes, so it is not disposed there either; left alone it would sit
    // in the scene doing nothing forever. Disposed only now, after the merge, and with
    // doNotRecurse: true so it can never cascade — AbstractMesh.dispose() recurses into
    // descendants by default, and calling it on __root__ *before* merging once disposed every
    // part along with it, which read back during the merge as "positions are required": the
    // very thing this template exists to build had already been torn down by the cleanup step
    // meant to run after it.
    const keep = new Set<AbstractMesh>(parts);
    for (const mesh of result.meshes) {
      if (!keep.has(mesh)) {
        mesh.dispose(true);
      }
    }

    merged.name = `model-template-${key}`;

    if (options?.normalize ?? true) {
      normalizeAndCenter(merged, key);
    } else {
      // An avatar part (or any other caller that asked to skip normalisation — LoadModelOptions)
      // keeps the mesh exactly as authored: no scale correction, and — deliberately, not merely
      // as a side effect of skipping the scale step — no re-centring on the bounding box either.
      // MergeMeshes above has already baked every part's own authored world transform into the
      // vertex data, so nothing further needs baking here; only the bounding info cache needs a
      // refresh, the same first step normalizeAndCenter itself always takes, so a freshly built
      // template's bounds are accurate immediately regardless of which branch ran.
      merged.refreshBoundingInfo();
    }

    // The template is never itself rendered — NodeMeshFactory clones it per node — and its
    // imported material (PBR; textureless on all eight of today's models, but not guaranteed in
    // general) is discarded rather than kept: every node in this project owns a StandardMaterial
    // it constructs itself, which is what keeps setHeldLook and colourFor working unchanged for
    // a model-backed node exactly as they already do for a procedural one — NodeMeshFactory
    // builds a fresh material either way, after cloning.
    //
    // Skipped when preserveMaterials is set: an avatar part's whole reason for asking for it is
    // to keep its authored Mat.Avatar.Body / Mat.Avatar.Tint slots alive past this point, so
    // AvatarManager.ts can find, clone and (for the Tint slot) recolour them per peer. The
    // template itself is still never rendered directly — cloneModelTemplate's own caller clones
    // this material (or replaces it) exactly as it already clones the mesh.
    if (!(options?.preserveMaterials ?? false)) {
      merged.material?.dispose(false, true);
      merged.material = null;
    }

    // Disabled and unpickable so the template itself can never be grabbed, drawn to, or rendered
    // while it sits here as a clone source. cloneModelTemplate() turns both back on for the
    // clone — cloning does not reset either for free.
    merged.setEnabled(false);
    merged.isPickable = false;

    return merged;
  }
}

/**
 * Clones a preloaded template into a real, independent node mesh.
 *
 * Two CLAUDE.md gotchas, both real bugs elsewhere in this project before they were pinned down.
 * `Mesh.clone(name, newParent)` does not clear the mesh's parent when `newParent` is `null` — it
 * copies whatever `_copySource` (here, the template) already had, before the constructor's own
 * `null` handling ever runs — so unparenting has to be its own statement after `.clone()`
 * returns, not trusted to the argument. And `setEnabled`/`isPickable` are mesh state the clone
 * inherits from the template, which is deliberately disabled and unpickable (see buildTemplate)
 * — both have to be explicitly turned back on here, or a model-backed node would be invisible
 * and ungrabbable.
 */
export function cloneModelTemplate(template: Mesh, name: string): Mesh {
  const clone = template.clone(name, null);

  clone.parent = null;
  clone.setEnabled(true);
  clone.isPickable = true;

  return clone;
}

/**
 * Centres a mesh's geometry on its own origin and uniformly scales it so its largest bounding
 * axis becomes TARGET_SIZE, baking both into the vertex data rather than leaving them as
 * mesh.position/.scaling.
 *
 * Baking matters: NodeMeshFactory.applyTransform sets a node mesh's position and scaling with a
 * hard `.set()`/`.setAll()` on every create() and refresh(), model-backed or not. A
 * transform-based centering would be silently overwritten the instant a node's own transform
 * was applied — baking it into the vertices instead survives that, the same reason a procedural
 * silhouette is authored centred on its own origin to begin with.
 *
 * Reads local (untransformed) bounds rather than world ones, which is the space the resulting
 * translation and scale are applied in — safe to conflate with world space here only because
 * the mesh a caller passes in is always freshly returned by Mesh.MergeMeshes, which always
 * starts a merged mesh at identity position/rotation/scaling.
 */
function normalizeAndCenter(mesh: Mesh, key: string): void {
  mesh.refreshBoundingInfo();
  const box = mesh.getBoundingInfo().boundingBox;
  const extent = box.maximum.subtract(box.minimum);
  const largestAxis = Math.max(extent.x, extent.y, extent.z);

  if (largestAxis <= 1e-6) {
    throw new Error('glb geometry has no meaningful size (degenerate bounding box).');
  }

  const scale = TARGET_SIZE / largestAxis;

  if (scale < NORMAL_SCALE_FACTOR.min || scale > NORMAL_SCALE_FACTOR.max) {
    console.warn(
      `Model "${key}.glb" needed a ${scale.toFixed(3)}x scale to reach ${TARGET_SIZE}m ` +
        `(authored largest axis: ${largestAxis.toFixed(3)}m) — its authored scale may be a ` +
        'mistake rather than a deliberate choice.',
    );
  }

  const center = box.minimum.add(box.maximum).scale(0.5);

  // Scale is applied to a point before translation in Babylon's TRS composition, so the centre
  // has to be shrunk by the same factor before it is negated into position — otherwise the
  // *scaled* centre would not land back on the origin.
  mesh.position.copyFrom(center.scale(-scale));
  mesh.scaling.setAll(scale);
  mesh.bakeCurrentTransformIntoVertices();
}

/** Real bytes over the wire via `fetch`, not Babylon's own URL loading (`Tools.LoadFile`, which
 *  goes through `XMLHttpRequest`) — deliberately, so a 404 is a plain, cheap, checkable
 *  `!response.ok` rather than an exception dug out of a loader's own error wrapping. Any network
 *  failure or non-2xx status reads as "no model for this key," which is correct for the common
 *  case (a type with no backing file at all) and merely conservative for a real network problem,
 *  since the fallback either way is the same procedural silhouette.
 *
 *  `response.ok` alone is not enough, though — confirmed running this for real against the dev
 *  server, not assumed: Vite answers *every* unmatched path with its own `index.html`, 200 OK,
 *  the same SPA-fallback behaviour a client-side router needs on a real page reload. Against
 *  that, every one of the thirteen non-model-backed catalogue types "resolved" to 23KB of HTML,
 *  each logging the misleading-but-harmless "was found but could not be used" warning once
 *  Babylon's loader rejected it. Checking the glTF magic bytes directly (isGlbMagic) answers the
 *  actual question — "did this path really return a model" — independent of what a given static
 *  host's 404 behaviour happens to be, dev server or production. */
async function fetchModelBytes(path: string): Promise<Uint8Array | undefined> {
  let response: Response;

  try {
    response = await fetch(path);
  } catch {
    return undefined;
  }

  if (!response.ok) {
    return undefined;
  }

  const bytes = new Uint8Array(await response.arrayBuffer());
  return isGlbMagic(bytes) ? bytes : undefined;
}

/** A valid .glb's first four bytes are always the ASCII magic "glTF" (0x67 0x6C 0x54 0x46). */
function isGlbMagic(bytes: Uint8Array): boolean {
  return bytes[0] === 0x67 && bytes[1] === 0x6c && bytes[2] === 0x54 && bytes[3] === 0x46;
}
